// Voice service for English text-to-speech functionality
class VoiceService {
  constructor() {
    this.synthesis = window.speechSynthesis;
    this.currentUtterance = null;
    this.isPlaying = false;
    this.isPaused = false;
    
    // Voice settings
    this.settings = {
      rate: 0.8,        // Speech rate (0.1 to 10)
      pitch: 1.0,       // Voice pitch (0 to 2)
      volume: 1.0,      // Volume (0 to 1)
      language: 'en-US', // English language
      voice: null       // Will be set to best available English voice
    };
    
    this.initializeVoice();
  }

  // Initialize and find the best English voice
  initializeVoice() {
    if (!this.synthesis) {
      console.warn('Speech synthesis not supported in this browser');
      return;
    }

    // Wait for voices to load
    const loadVoices = () => {
      const voices = this.synthesis.getVoices();
      
      // Find the best English voice
      const englishVoices = voices.filter(voice => 
        voice.lang.startsWith('en') || 
        voice.name.includes('English') ||
        voice.name.includes('US') ||
        voice.name.includes('UK')
      );
      
      if (englishVoices.length > 0) {
        // Prefer voices with better quality indicators
        const preferredVoice = englishVoices.find(voice => 
          voice.name.includes('Google') || 
          voice.name.includes('Microsoft') ||
          voice.name.includes('Neural') ||
          voice.name.includes('Enhanced')
        ) || englishVoices[0];
        
        this.settings.voice = preferredVoice;
        console.log('Selected English voice:', preferredVoice.name);
      } else {
        console.warn('No English voices found, using default voice');
      }
    };

    // Load voices immediately if available, or wait for the event
    if (this.synthesis.getVoices().length > 0) {
      loadVoices();
    } else {
      this.synthesis.addEventListener('voiceschanged', loadVoices);
    }
  }

  // Speak the given text
  speak(text, options = {}) {
    if (!this.synthesis || !text) {
      return Promise.reject('Speech synthesis not available or no text provided');
    }

    // Stop any current speech
    this.stop();

    return new Promise((resolve, reject) => {
      try {
        this.currentUtterance = new SpeechSynthesisUtterance(text);
        
        // Apply settings
        this.currentUtterance.rate = options.rate || this.settings.rate;
        this.currentUtterance.pitch = options.pitch || this.settings.pitch;
        this.currentUtterance.volume = options.volume || this.settings.volume;
        this.currentUtterance.lang = options.language || this.settings.language;
        
        // Set voice if available
        if (this.settings.voice) {
          this.currentUtterance.voice = this.settings.voice;
        }

        // Event handlers
        this.currentUtterance.onstart = () => {
          this.isPlaying = true;
          this.isPaused = false;
        };

        this.currentUtterance.onend = () => {
          this.isPlaying = false;
          this.isPaused = false;
          this.currentUtterance = null;
          resolve();
        };

        this.currentUtterance.onerror = (event) => {
          this.isPlaying = false;
          this.isPaused = false;
          this.currentUtterance = null;
          reject(event.error);
        };

        this.currentUtterance.onpause = () => {
          this.isPaused = true;
        };

        this.currentUtterance.onresume = () => {
          this.isPaused = false;
        };

        // Start speaking
        this.synthesis.speak(this.currentUtterance);
        
      } catch (error) {
        reject(error);
      }
    });
  }

  // Pause current speech
  pause() {
    if (this.synthesis && this.isPlaying && !this.isPaused) {
      this.synthesis.pause();
    }
  }

  // Resume paused speech
  resume() {
    if (this.synthesis && this.isPaused) {
      this.synthesis.resume();
    }
  }

  // Stop current speech
  stop() {
    if (this.synthesis) {
      this.synthesis.cancel();
      this.isPlaying = false;
      this.isPaused = false;
      this.currentUtterance = null;
    }
  }

  // Toggle play/pause
  togglePlayPause() {
    if (this.isPlaying && !this.isPaused) {
      this.pause();
    } else if (this.isPaused) {
      this.resume();
    }
  }

  // Update voice settings
  updateSettings(newSettings) {
    this.settings = { ...this.settings, ...newSettings };
  }

  // Get current status
  getStatus() {
    return {
      isPlaying: this.isPlaying,
      isPaused: this.isPaused,
      settings: { ...this.settings }
    };
  }

  // Check if speech synthesis is supported
  isSupported() {
    return !!this.synthesis;
  }

  // Get available voices
  getAvailableVoices() {
    if (!this.synthesis) return [];
    return this.synthesis.getVoices();
  }

  // Get English voices specifically
  getEnglishVoices() {
    const voices = this.getAvailableVoices();
    return voices.filter(voice => 
      voice.lang.startsWith('en') || 
      voice.name.includes('English') ||
      voice.name.includes('US') ||
      voice.name.includes('UK')
    );
  }

  // Keep the old method name for compatibility
  getChineseVoices() {
    return this.getEnglishVoices();
  }
}

// Create and export a singleton instance
const voiceService = new VoiceService();
export default voiceService;
